"use client";

import type { FC, Ref } from "react";
import { FormRowGroup } from "@huub-web/ui";

import { TemplatesParser } from "../../components";
import type { TemplatesProviderState } from "../../contexts";
import { useMaybeTemplates } from "../../hooks/useTemplates";
import type { TemplateCollection } from "../../types";
import { isConditionalIf } from "../../utils";

export interface FormRowGroupTemplateProps {
  /**
   * Templates to render inside the FormRowGroup
   */
  templates: TemplateCollection;
}

/**
 * Enhances templates by auto-assigning equal widths to `formInput` children
 * that don't have an explicit `width` set. Respects `visibleIf` rules so
 * only currently-visible inputs are counted for the width calculation.
 */
const useAutoWidthTemplates = ({
  templates,
  dataFieldValues,
}: {
  templates: TemplateCollection;
  dataFieldValues: TemplatesProviderState["dataFieldValues"];
}): TemplateCollection => {
  const autoWidthCandidates = templates.filter(
    (t) => t.templateKey === "formInput" && !t.data?.width,
  );

  const visibleCount = autoWidthCandidates.filter((t) =>
    isConditionalIf(t.data?.visibleIf, dataFieldValues),
  ).length;

  if (visibleCount <= 1) return templates;

  const autoWidth = `${Math.floor(100 / visibleCount)}%`;
  const candidateIds = new Set(autoWidthCandidates.map((t) => t.id));

  return templates.map((t) =>
    candidateIds.has(t.id)
      ? { ...t, data: { ...t.data, width: autoWidth } }
      : t,
  );
};

export const FormRowGroupTemplate: FC<
  FormRowGroupTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({ templates, ref }) => {
  const { dataFieldValues } = useMaybeTemplates() ?? {
    dataFieldValues: {} as TemplatesProviderState["dataFieldValues"],
  };
  const enhancedTemplates = useAutoWidthTemplates({
    templates,
    dataFieldValues,
  });

  return (
    <FormRowGroup ref={ref}>
      <TemplatesParser level={1} templates={enhancedTemplates} />
    </FormRowGroup>
  );
};
